import React, { useState, useMemo, useEffect } from "react";
import { motion } from 'framer-motion';
import { Users, Shield, Clock, Zap, Code, Server } from 'lucide-react';

const About = () => {
        useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }, []);

  const stats = [
    { icon: Users, label: 'Integraciones Activas', value: '500+', description: 'Empresas confían en nuestras APIs' },
    { icon: Shield, label: 'Documentos Procesados', value: '10M+', description: 'Transacciones seguras mensuales' },
    { icon: Clock, label: 'Tiempo de Respuesta', value: '<100ms', description: 'Latencia promedio' },
    { icon: Zap, label: 'Disponibilidad', value: '99.9%', description: 'Uptime garantizado' }
  ];

  const features = [
    {
      icon: Code,
      title: 'Desarrollo Simplificado',
      description: 'SDKs en múltiples lenguajes y documentación clara para integración rápida.'
    },
    {
      icon: Server,
      title: 'Infraestructura Robusta',
      description: 'Servidores de alta disponibilidad con redundancia y escalado automático.'
    },
    {
      icon: Shield,
      title: 'Seguridad Enterprise',
      description: 'Cifrado end-to-end, autenticación JWT y cumplimiento de estándares SUNAT.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-blue-50 relative overflow-hidden border-t border-gray-200">
      {/* Efectos de fondo consistentes con Hero */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]">
          <div className="absolute inset-0 bg-[linear-gradient(#000_1px,transparent_1px),linear-gradient(90deg,#000_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>
        
        {/* Elementos flotantes */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-500/20 rounded-full"
            initial={{
              x: Math.random() * window.innerWidth,
              y: Math.random() * window.innerHeight / 2 + 100,
            }}
            animate={{
              scale: [1, 1.8, 1],
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 4,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10">
        {/* Encabezado */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-50px" }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center space-x-2 bg-blue-500/10 border border-blue-500/20 rounded-lg px-4 py-2 mb-6"
          >
            <Code className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-mono text-blue-700">TECHNOLOGY & INNOVATION</span>
          </motion.div>

          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Especialistas en{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
              APIs SUNAT
            </span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            Líderes en integración tributaria digital, proporcionando soluciones robustas 
            y seguras para miles de empresas en Perú.
          </p>
        </motion.div>

        {/* Contenido principal */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">
                Infraestructura de Clase Enterprise
              </h3>
              <p className="text-gray-700 leading-relaxed">
                En Visioner7, hemos desarrollado APIs de alta performance para toda la cadena 
                de procesos SUNAT. Desde consultas RUC/DNI en tiempo real hasta envío masivo 
                de documentos electrónicos con garantía de entrega.
              </p>
            </div>

            <div className="space-y-4">
              <h4 className="text-lg font-semibold text-gray-900">Tecnología Avanzada</h4>
              <p className="text-gray-700 leading-relaxed">
                Nuestra plataforma utiliza arquitectura microservicios, balanceo de carga 
                inteligente y replicación de datos en tiempo real para asegurar máxima 
                disponibilidad y performance.
              </p>
            </div>

            {/* Características destacadas */}
            <div className="grid gap-4 mt-6">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3 p-3 rounded-lg hover:bg-white/50 transition-colors"
                  >
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <Icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-900 text-sm">{feature.title}</h5>
                      <p className="text-gray-600 text-xs mt-1">{feature.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="bg-gradient-to-br from-blue-600/5 to-cyan-600/5 rounded-2xl p-8 border border-gray-200/50">
              <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <div className="text-xs text-gray-500 font-mono ml-2">api-demo.js</div>
                </div>
                <pre className="text-xs text-gray-700 font-mono overflow-x-auto">
{`// Consulta RUC simplificada
const response = await sunatAPI.ruc.get('20512345678');

// Envío de factura electrónica
const ticket = await sunatAPI.documents.send({
  tipo: 'FACTURA',
  emisor: '20512345678',
  cliente: '10456789012',
  monto: 1500.00
});

// Webhook para CDR
app.post('/webhook/cdr', (req, res) => {
  console.log('CDR recibido:', req.body);
  res.status(200).send('OK');
});`}
                </pre>
              </div>
            </div>
            
            {/* Elemento decorativo */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              viewport={{ once: true }}
              className="absolute -bottom-4 -right-4 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg"
            >
              Código de Ejemplo
            </motion.div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-white border border-gray-200 rounded-xl p-6 text-center hover:border-blue-300 hover:shadow-md transition-all duration-300"
                whileHover={{ y: -2 }}
              >
                <div className="w-12 h-12 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-100 transition-colors">
                  <Icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                <p className="font-semibold text-gray-700 text-sm mb-1">{stat.label}</p>
                <p className="text-gray-500 text-xs">{stat.description}</p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA Final */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-2">¿Listo para comenzar?</h3>
            <p className="text-gray-600 mb-6">Únete a miles de desarrolladores que ya usan nuestras APIs</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium text-sm border border-blue-700 hover:bg-blue-700 transition-colors"
              >
                Comenzar Integración
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="border border-gray-300 text-gray-700 bg-white px-6 py-3 rounded-lg font-medium text-sm hover:border-gray-400 transition-colors"
              >
                Contactar Ventas
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;